import { FieldCacheService, NSARouterMakerResult } from 'namirasoft-account-react';
import { EncodingOperation } from 'namirasoft-core';
import { SlotEntityType } from 'namirasoft-field';
import { UserFullRow } from 'namirasoft-password';
import { BooleanFormatter, EncryptionOperation, NSButton, NSButtonBlue, NSRow, NSTable, SensitiveFormatter, StringFormatter, StringFormatterSizeType, URLFormatter } from 'namirasoft-site-react';
import { Component, createRef } from 'react';
import { ImportedRow } from '../pages/ImportPage';
import { PasswordRouterProps } from '../PasswordRouterProps';
import { Storage } from '../Storage';
import Styles from '../pages/SetupWizard.module.css';

export interface ImportPasswordStepProps extends NSARouterMakerResult, PasswordRouterProps
{
    user: UserFullRow;
    importedRows: ImportedRow[];
    onBack: () => void;
}

interface ImportPasswordStepState
{
    imported_rows: ImportedRow[];
}

class ImportPasswordStep extends Component<ImportPasswordStepProps, ImportPasswordStepState>
{
    NSTable_Ref = createRef<NSTable<ImportedRow>>();

    constructor(props: ImportPasswordStepProps)
    {
        super(props);
        this.state = { imported_rows: props.importedRows };
        this.submit = this.submit.bind(this);
    }

    override componentDidUpdate(prevProps: ImportPasswordStepProps)
    {
        if (prevProps.importedRows !== this.props.importedRows)
        {
            this.setState({ imported_rows: this.props.importedRows }, () =>
            {
                this.NSTable_Ref.current?.reload(null, null);
            });
        }
    }

    async submit(onFinished: () => void)
    {
        try
        {
            let rows = this.NSTable_Ref.current?.getSelectedRows() ?? [];
            rows = rows.filter(row => !row.imported);

            if (rows.length === 0)
            {
                this.props.notifier.onError('Select at least one unimported row');
                return onFinished();
            }

            let field_rows = await FieldCacheService.get(
                this.props.account.token_manager,
                this.props.product_id,
                this.props.notifier.onError,
            ).get();

            let getEntryCategories = (category_id?: string) =>
            {
                if (!category_id)
                    return [];

                let category = field_rows.categories.find(category => category.id === category_id);
                let slot = field_rows.slots.find(slot =>
                    slot.entity.trim().toLowerCase() === 'entry' &&
                    slot.entity_type === SlotEntityType.CategoryScope &&
                    slot.entity_id === category?.category_scope_id);

                return slot ? [{ slot_id: slot.id, category_id }] : [];
            };

            for (const row of rows)
            {
                if (row.imported)
                    continue;

                await this.props.servers.password.entry.Create({
                    workspace_id: "",
                    name: row.name,
                    url: row.url,
                    entry_line: [
                        { name: 'Username', value: row.username, sensitive: false },
                        { name: 'Password', value: row.password, sensitive: true },
                    ],
                    hash: row.hash,
                    description: row.description ?? null,
                    entry_category: getEntryCategories(row.category_id),
                    entry_field: [],
                    entry_tag: [],
                });

                this.setState(
                    (prev) => ({ imported_rows: prev.imported_rows.map(r => r.id === row.id ? { ...r, imported: true } : r) }),
                    () => this.NSTable_Ref.current?.reload(null, null),
                );
            }

            this.props.notifier.onSuccess('Your password(s) have been added successfully.');
            onFinished();
        }
        catch (error)
        {
            onFinished();
        }
    }

    override render()
    {
        return (
            <div style={{ overflowX: 'auto' }}>
                <NSTable<ImportedRow>
                    ref={this.NSTable_Ref}
                    checkbox
                    name="import"
                    getRowKey={(item) => String(item.value.id)}
                    columns={[
                        { table: { name: '', text: '' }, index: 0, type: 'Boolean', name: 'imported', text: 'Imported', formatter: new BooleanFormatter('✅', '❌') },
                        { table: { name: '', text: '' }, index: 1, type: 'String', name: 'name', text: 'Name', formatter: new StringFormatter() },
                        { table: { name: '', text: '' }, index: 2, type: 'String', name: 'username', text: 'Username', formatter: new StringFormatter() },
                        {
                            table: { name: '', text: '' }, index: 3, type: 'String', name: 'password', text: 'Password', formatter: new SensitiveFormatter(async (value) =>
                            {
                                const master_key = await Storage.getMasterKey(this.props.user.id);
                                const encoded_value = await EncryptionOperation.AES256GCMDecrypt(master_key, value) as string;
                                return EncodingOperation.Base64Decode(encoded_value);
                            }, () => true)
                        },
                        { table: { name: '', text: '' }, index: 4, type: 'String', name: 'url', text: 'URL', formatter: new URLFormatter() },
                        { table: { name: '', text: '' }, index: 5, type: 'String', name: 'category_name', text: 'Category', formatter: new StringFormatter() },
                        { table: { name: '', text: '' }, index: 6, type: 'String', name: 'description', text: 'Description', formatter: new StringFormatter(StringFormatterSizeType.Description) },
                    ]}
                    getRows={async () => ({
                        count: this.state.imported_rows.length,
                        rows: this.state.imported_rows,
                    })}
                    getRowCheckboxDisabled={(item) => item.value.imported}
                />
                <NSRow style={{ position: 'relative', marginTop: '1.5rem' }}>
                    <NSButtonBlue
                        title="Import"
                        onClick={{ action: this.submit }}
                    />
                    <NSButton
                        title=""
                        icon={{ src: 'https://static.namirasoft.com/image/concept/arrow/left/white-blue.svg' }}
                        onClick={{ showLoading: false, action: () => this.props.onBack() }}
                        classList={[Styles.ns_arrow_button]}
                    />
                </NSRow>
            </div>
        );
    }
}

export default ImportPasswordStep;